import moment from "moment";
import { type LocalDatabaseCustomer } from "../../../Data/_Bases/BaseCustomerRepository";
import { type LocalDatabasePromotion } from "../../../Data/_Bases/BasePromotionRepository";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { type SaleProduct } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { getCurrencyDecimalPlaces } from "../../Generic/Numbers";
import { getLogLevel, LogLevel } from "../../Platform/Logging";
import { PROMOTION_EVENTS, PromotionCalculationInvalid } from "./PromotionEvents";
import { PromotionManager } from "./PromotionManager";
import type { SalePromotionProduct } from "./PromotionUtilities";

export class PromotionWorkerHandler {
    protected manager: PromotionManager;
    protected hasUsedGreedyCalculation: boolean;
    protected ready: boolean;
    protected readyQueue: Array<() => void>;

    constructor(protected onReady: () => void, protected isWorker = false) {
        this.manager = new PromotionManager({
            redemption_rate      : 0,
            earn_rate            : 0,
            use_quantity_rate    : false,
            cross_promotion_count: true,
            family_distribution  : "evenly",
            decimal_places       : getCurrencyDecimalPlaces(),
            rounding_mode        : "redistribute",
        });

        PromotionManager.onGreedyCalculationUsed = () => {
            this.hasUsedGreedyCalculation = true;
        };

        this.hasUsedGreedyCalculation = false;
        this.ready = false;
        this.readyQueue = [];

        void this.setupDataSource();
    }

    protected setupDataSource = (): Promise<void> => {
        return determineDataSource()
            .then((source) => {
                this.manager.setProductRepository(source.products);
                this.manager.setPromotionRepository(source.promotions);
                this.manager.setLoyaltyRepository(source.loyalty);
                this.manager.setPriceListRepository(source.priceLists);
                this.ready = true;
                this.onReady();

                for(let i = 0, l = this.readyQueue.length; i < l; i++) {
                    this.readyQueue[i]();
                }

                this.readyQueue = [];
            });
    };

    protected onTransactionFinished(): void {
        this.manager.setCurrentTime(moment());
    }

    protected onSyncFinished(): void {
        this.manager.productRepository.clearCache();

        if(this.isWorker) {
            void this.manager.promotionRepository.mapToCache();
        }
    }

    protected updateSetting(setting: string, newValue: string | number | boolean): void {
        if(setting === "timeout_enabled") {
            this.manager.setTimeoutEnabled(newValue as boolean);

            return;
        }

        this.manager.updateSettings({
            [setting]: newValue,
        });
    }

    protected async calculate(
        onComplete: (data: {
            products: Array<SalePromotionProduct>;
            usedGreedyCalculation: boolean;
        } | {
            error: true;
            object: Error;
            isInvalidCalculation?: boolean;
        }) => void,
        products: Array<SaleProduct>,
        customer: LocalDatabaseCustomer | false,
        outlet: string | null,
        costCalculationMethod: "last" | "mixed" | "average",
        useGlobalCost: boolean,
        applicablePromotions: Array<string>
    ): Promise<void> {
        if(!this.ready) {
            const id = Date.now();

            if(getLogLevel() >= LogLevel.NORMAL) {
                console.info("Promotion worker handler is not yet ready, adding to ready queue", id);
            }

            await new Promise<void>(res => {
                this.readyQueue.push(res);
            });

            if(getLogLevel() >= LogLevel.NORMAL) {
                console.info("Promotion worker handler received ready signal, starting to process", id);
            }
        }

        let results: Array<SalePromotionProduct>;

        this.hasUsedGreedyCalculation = false;
        this.manager.calculatePrices(
            products as Array<SaleProduct & {
                case_quantity: number;
                is_case: boolean;
            }>,
            customer === false ? null : customer,
            outlet,
            costCalculationMethod,
            useGlobalCost,
            applicablePromotions,
            (products) => {
                results = products;
            }
        )
            .then(() => {
                if(typeof results === "undefined") {
                    onComplete({ // This occurs when a second promotion calculation is started during the first
                        error               : true,
                        object              : new PromotionCalculationInvalid(),
                        isInvalidCalculation: true,
                    });
                } else {
                    onComplete({
                        usedGreedyCalculation: this.hasUsedGreedyCalculation,
                        products             : results,
                    });
                }

                if(this.hasUsedGreedyCalculation) {
                    this.hasUsedGreedyCalculation = false;
                }
            })
            .catch(err => {
                console.error("Received an error while calculating a promotion", err);
                onComplete({
                    error : true,
                    object: err,
                });
            });
    }

    protected everydayPrice(
        onComplete: (price: number) => void,
        id: string,
        quantity: number
    ): void {
        this.manager.everydayPrice(id, quantity)
            .then(onComplete);
    }

    protected calculateLoyalty(
        onComplete: (products: Array<SaleProduct>) => void,
        products: Array<SaleProduct>,
        customer: LocalDatabaseCustomer
    ): void {
        this.manager.calculateLoyalty(products, customer)
            .then(() => {
                onComplete(products);
            });
    }

    protected simulate(
        onComplete: () => void,
        originalId: null | string,
        promotion: LocalDatabasePromotion | null
    ): void {
        determineDataSource()
            .then(source => source.promotions.simulate(originalId, promotion))
            .then(onComplete);
    }

    public handle(
        data: {
            type: PROMOTION_EVENTS;
            message?: any;
        },
        complete: (() => any) | null
    ): void {
        if(!data.type) {
            return;
        }

        switch(data.type) {
            case PROMOTION_EVENTS.TRANSACTION_FINISHED:
                this.onTransactionFinished();
                complete?.();
                break;
            case PROMOTION_EVENTS.UPDATE_SETTINGS:
                this.updateSetting(data.message.setting, data.message.value);
                complete?.();
                break;
            case PROMOTION_EVENTS.CALCULATE:
                void this.calculate(
                    complete as () => any,
                    data.message.products,
                    data.message.customer,
                    data.message.outlet,
                    data.message.costCalculationMethod,
                    data.message.useGlobalCost,
                    data.message.applicable
                );
                break;
            case PROMOTION_EVENTS.EVERYDAY_PRICE:
                this.everydayPrice(
                    complete as () => any,
                    data.message.id,
                    data.message.quantity
                );
                break;
            case PROMOTION_EVENTS.CALCULATE_LOYALTY:
                this.calculateLoyalty(
                    complete as () => any,
                    data.message.products,
                    data.message.customer
                );
                break;
            case PROMOTION_EVENTS.SETUP_DATA_SOURCES:
                this.setupDataSource()
                    .then(() => complete?.());
                break;
            case PROMOTION_EVENTS.SIMULATE:
                this.simulate(
                    complete as () => any,
                    data.message.originalId,
                    data.message.promotion
                );
                break;
            case PROMOTION_EVENTS.SYNC_FINISHED:
                this.onSyncFinished();
                complete?.();
                break;
            default:
                // Do nothing, was confusing the support team when this was logged
        }
    }
}
